import React, { FC, useEffect, useState } from 'react';
import { BankAccount, CashInSettings } from 'interfaces';
import { getCashInSettings, getIBans } from 'services';
import { BsChevronDown } from 'react-icons/bs';
import { Spin } from 'antd';
import { IBanSelectionModal } from '../../iBanSelectionModal/iBanSelectionModal.index';
import { Button, Input } from '30pay/components';
import { TbCopy } from 'react-icons/tb';
import { copyToClipboard, deleteCommaSeparator, detectBankByName, faNumToEnNum } from 'scripts';
import CoinIcon from 'utilities/components/coinIcon/coinIcon.index';

interface Props {}

export const BuyPaymentCashInMethod: FC<Props> = (props) => {
    const [iBansLoading, setIBansLoading] = useState(true);
    const [iBans, setIBans] = useState<Omit<BankAccount, 'cardNumber'>[]>();
    const [selectedIBan, setSelectedIBan] = useState<Omit<BankAccount, 'cardNumber'>>();
    const [cashInSettingsLoading, setCashInSettingsLoading] = useState<boolean>(false);
    const [cashInSettings, setCashInSettings] = useState<CashInSettings>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [amount, setAmount] = useState<number>();

    const onIBanSelection = (iBan: Omit<BankAccount, 'cardNumber'>) => {
        setSelectedIBan(iBan);
        setModalOpen(false);
        setCashInSettings(undefined);
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = deleteCommaSeparator(faNumToEnNum(e.target.value));
        const numericValue = Number(inputValue);

        if (inputValue === '' || isNaN(numericValue)) {
            setAmount(undefined);
        } else {
            setAmount(numericValue);
        }
    };

    const onGetCashInSettings = () => {
        getCashInSettings(
            setCashInSettingsLoading,
            {
                iBanId: selectedIBan?.id,
                amount: amount,
                platform: 1,
            },
            setCashInSettings,
        );
    };

    useEffect(() => {
        getIBans(setIBansLoading, setIBans, setSelectedIBan);
    }, []);

    return (
        <div className={'flex flex-col'}>
            {iBans && (
                <IBanSelectionModal open={modalOpen} setOpen={setModalOpen} iBans={iBans} onSelect={onIBanSelection} />
            )}
            <label className={'max-lg:text-sm'}>شماره شبا:</label>
            <Spin spinning={iBansLoading}>
                <div
                    onClick={() => setModalOpen(true)}
                    className={
                        'flex justify-between items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 w-full max-lg:h-12 max-lg:text-sm mt-2 clickable'
                    }
                >
                    {selectedIBan && (
                        <>
                            <div className={'flex items-center gap-4'}>
                                <img
                                    src={detectBankByName(selectedIBan?.bank?.name)?.bank_logo}
                                    className={'w-6 h-6'}
                                />
                                {selectedIBan?.bank?.name}
                            </div>
                            {selectedIBan?.iBan}
                            <BsChevronDown />
                        </>
                    )}
                </div>
            </Spin>
            <label className={'max-lg:text-sm mt-4'}>مبلغ واریزی‌ (تومان):</label>
            <Input
                placeholder={'مبلغ واریزی را وارد نمایید...'}
                className={'mt-2'}
                inputMode={'numeric'}
                type={'text'}
                value={amount?.toLocaleString()}
                onChange={handleAmountChange}
                suffix={<CoinIcon symbol={'IRT'} className={'mr-2'} />}
                dir={amount ? 'ltr' : 'rtl'}
            />
            <Button
                className={'mt-6'}
                loading={cashInSettingsLoading}
                type={'primary'}
                disabled={cashInSettingsLoading || !amount || !selectedIBan}
                onClick={onGetCashInSettings}
            >
                دریافت اطلاعات واریز
            </Button>
            {cashInSettings && (
                <div className={'flex flex-col p-4 rounded-lg bg-background mt-6'}>
                    <div
                        className={
                            'flex items-center justify-between bg-white h-12 px-2 border border-primary-20 rounded-lg'
                        }
                    >
                        <span className={'font-bold text-base text-black-100'}>شناسه واریز</span>
                        <span className={'font-bold text-xl text-black-100'}>{cashInSettings?.depositId}</span>
                        <Button
                            type={'primary'}
                            icon={<TbCopy />}
                            className={'h-8 rounded-lg'}
                            onClick={() => copyToClipboard(cashInSettings?.depositId, 'شناسه واریز')}
                        >
                            کپی
                        </Button>
                    </div>
                    <span className={'font-bold text-darkPurple-100 text-base mt-5'}>اطلاعات مقصد</span>
                    <ul className={'flex flex-col gap-2 mt-2'}>
                        <li className={'flex items-center justify-between'}>
                            <span className={'font-bold text-sm'}>شماره شبا مقصد</span>
                            <span className={'flex items-center gap-2 font-bold text-sm'}>
                                {cashInSettings?.iBan}
                                <TbCopy
                                    className={'clickable'}
                                    onClick={() => copyToClipboard(cashInSettings?.iBan, 'شماره شبا حساب مقصد')}
                                />
                            </span>
                        </li>
                        <li className={'flex items-center justify-between'}>
                            <span className={'font-bold text-sm'}>شماره حساب مقصد</span>
                            <span className={'flex items-center gap-2 font-bold text-sm'}>
                                {cashInSettings?.accountNumber}
                                <TbCopy
                                    className={'clickable'}
                                    onClick={() => copyToClipboard(cashInSettings?.accountNumber, 'شماره حساب مقصد')}
                                />
                            </span>
                        </li>
                        <li className={'flex items-center justify-between'}>
                            <span className={'font-bold text-sm'}>نام صاحب حساب مقصد</span>
                            <span className={'flex items-center gap-2 font-bold text-sm'}>
                                {cashInSettings?.bankAccountOwner}
                            </span>
                        </li>
                        <li className={'flex items-center justify-between'}>
                            <span className={'font-bold text-sm'}>نام بانک مقصد</span>
                            <span className={'flex items-center gap-2 font-bold text-sm'}>
                                {cashInSettings?.bankName}
                            </span>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};
